/**
 * SHELL — spoiler-free shareable result card.
 *
 * The card brags about the RESULT (streak, rank/percentile, finish time, a
 * Wordle-style emoji bar) WITHOUT revealing the course layout or the line a
 * player took — so sharing never spoils today's puzzle for friends who haven't
 * run yet. Rendered to a canvas (for image share / download) and mirrored as a
 * plain-text caption (for clipboard / Web Share text).
 *
 * Drawn entirely from numbers; it contains no screenshot of the 3D scene.
 */

import type { DateKey } from '@/types';
import type { ScoreMetrics } from '@/types';
import { formatTime, formatPercentile, formatRank, formatDateLabel } from '../ui/format';

export interface ShareData {
  readonly dateKey: DateKey;
  readonly metrics: ScoreMetrics;
  readonly streak: number;
  readonly rankGlobal?: number;
  readonly percentile?: number;
}

/** Day number since launch, for a compact "DailyRoll #123" headline. */
const EPOCH = Date.UTC(2026, 0, 1); // 2026-01-01
export function dayNumber(dateKey: DateKey): number {
  const t = Date.parse(`${dateKey}T00:00:00Z`);
  if (Number.isNaN(t)) return 0;
  return Math.floor((t - EPOCH) / 86_400_000) + 1;
}

/** A spoiler-free emoji "performance bar" derived only from the percentile. */
function emojiBar(percentile: number | undefined, checkpoints: number): string {
  const slots = 5;
  const filled = percentile === undefined
    ? Math.min(slots, Math.max(1, checkpoints))
    : Math.round((percentile / 100) * slots);
  return '🟦'.repeat(filled) + '⬛'.repeat(Math.max(0, slots - filled));
}

/** Plain-text caption for clipboard / share sheets. Spoiler-free by design. */
export function buildShareText(data: ShareData, url = 'https://dailyroll.app'): string {
  const lines: string[] = [];
  lines.push(`DailyRoll #${dayNumber(data.dateKey)}`);
  lines.push(emojiBar(data.percentile, data.metrics.checkpointsReached));
  const finished = data.metrics.finishTick >= 0;
  lines.push(finished ? `⏱ ${formatTime(data.metrics.finishTimeMs)}` : `🏁 ${data.metrics.checkpointsReached} checkpoints`);
  const pct = formatPercentile(data.percentile);
  if (pct) lines.push(`🌍 ${pct}`);
  else if (data.rankGlobal) lines.push(`🌍 ${formatRank(data.rankGlobal)}`);
  if (data.streak > 1) lines.push(`🔥 ${data.streak}-day streak`);
  lines.push(url);
  return lines.join('\n');
}

const W = 1080;
const H = 1080;

/** Render the card to a canvas. Caller may toDataURL / toBlob it. */
export function renderShareCanvas(data: ShareData): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');
  if (!ctx) return canvas;

  // Background gradient.
  const bg = ctx.createLinearGradient(0, 0, W, H);
  bg.addColorStop(0, '#0b0e14');
  bg.addColorStop(1, '#16203a');
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, W, H);

  // Decorative marble glow.
  const glow = ctx.createRadialGradient(W * 0.5, H * 0.32, 20, W * 0.5, H * 0.32, 280);
  glow.addColorStop(0, 'rgba(91,140,255,0.55)');
  glow.addColorStop(1, 'rgba(91,140,255,0)');
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, W, H);

  ctx.textAlign = 'center';
  ctx.fillStyle = '#eef2f8';

  ctx.font = '700 44px system-ui, sans-serif';
  ctx.fillText('DAILYROLL', W / 2, 130);

  ctx.font = '800 96px system-ui, sans-serif';
  ctx.fillText(`#${dayNumber(data.dateKey)}`, W / 2, 250);

  ctx.font = '500 34px system-ui, sans-serif';
  ctx.fillStyle = '#9aa7bd';
  ctx.fillText(formatDateLabel(data.dateKey), W / 2, 310);

  // Big stat: finish time or checkpoints.
  const finished = data.metrics.finishTick >= 0;
  ctx.fillStyle = '#eef2f8';
  ctx.font = '800 140px system-ui, sans-serif';
  ctx.fillText(finished ? formatTime(data.metrics.finishTimeMs) : `${data.metrics.checkpointsReached} CP`, W / 2, 560);
  ctx.font = '600 32px system-ui, sans-serif';
  ctx.fillStyle = '#66748c';
  ctx.fillText(finished ? 'FINISH TIME' : 'CHECKPOINTS REACHED', W / 2, 610);

  // Emoji-free pip bar (canvas) reflecting percentile.
  drawPips(ctx, data, 700);

  // Rank / percentile.
  const pct = formatPercentile(data.percentile);
  ctx.font = '700 48px system-ui, sans-serif';
  ctx.fillStyle = '#7aa2ff';
  ctx.fillText(pct ?? formatRank(data.rankGlobal), W / 2, 830);

  // Streak.
  if (data.streak > 0) {
    ctx.font = '800 64px system-ui, sans-serif';
    ctx.fillStyle = '#ffd25c';
    ctx.fillText(`🔥 ${data.streak}`, W / 2, 940);
    ctx.font = '600 30px system-ui, sans-serif';
    ctx.fillStyle = '#9aa7bd';
    ctx.fillText('DAY STREAK', W / 2, 985);
  }

  ctx.font = '600 30px system-ui, sans-serif';
  ctx.fillStyle = '#66748c';
  ctx.fillText('dailyroll.app · no spoilers', W / 2, H - 50);

  return canvas;
}

function drawPips(ctx: CanvasRenderingContext2D, data: ShareData, y: number): void {
  const slots = 5;
  const filled = data.percentile === undefined
    ? Math.min(slots, Math.max(1, data.metrics.checkpointsReached))
    : Math.round((data.percentile / 100) * slots);
  const size = 64;
  const gap = 28;
  const totalW = slots * size + (slots - 1) * gap;
  let x = (W - totalW) / 2 + size / 2;
  for (let i = 0; i < slots; i++) {
    ctx.beginPath();
    ctx.arc(x, y, size / 2, 0, Math.PI * 2);
    ctx.fillStyle = i < filled ? '#5b8cff' : '#243044';
    ctx.fill();
    x += size + gap;
  }
}

export interface ShareResult {
  readonly method: 'share' | 'clipboard' | 'download' | 'none';
}

/**
 * Share the card via the Web Share API (with image when supported), falling back
 * to clipboard text, then to a PNG download. Never throws.
 */
export async function shareResult(data: ShareData): Promise<ShareResult> {
  const text = buildShareText(data);
  const title = `DailyRoll #${dayNumber(data.dateKey)}`;

  // Try image share first (best on mobile).
  try {
    const canvas = renderShareCanvas(data);
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
    if (blob) {
      const file = new File([blob], `dailyroll-${data.dateKey}.png`, { type: 'image/png' });
      const navAny = navigator as Navigator & { canShare?: (d: ShareData2) => boolean };
      if (navAny.share && navAny.canShare?.({ files: [file] })) {
        await navAny.share({ title, text, files: [file] });
        return { method: 'share' };
      }
      if (navigator.share) {
        await navigator.share({ title, text });
        return { method: 'share' };
      }
    }
  } catch {
    /* user cancelled or unsupported — fall through */
  }

  // Clipboard text fallback.
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return { method: 'clipboard' };
    }
  } catch {
    /* fall through */
  }

  // PNG download fallback.
  try {
    const canvas = renderShareCanvas(data);
    const url = canvas.toDataURL('image/png');
    const a = document.createElement('a');
    a.href = url;
    a.download = `dailyroll-${data.dateKey}.png`;
    a.click();
    return { method: 'download' };
  } catch {
    return { method: 'none' };
  }
}

/** Structural type for navigator.share/canShare with files. */
interface ShareData2 {
  files?: File[];
  title?: string;
  text?: string;
}
